//eslint-disable-next-line
//@ts-nocheck

import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import useTranslation from 'next-translate/useTranslation';
import dynamic from 'next/dynamic';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

import { Button } from '@/atoms';

import CheckmarkCircle from '@/assets/icons/CheckmarkCircle.svg';
import WarningCircle from '@/assets/icons/WarningCircle.svg';
import { gtmPush, sha256 } from '@/utils';
import ContactFormLanding from '../contact-form-landing';
import ContactFormStandard from '../contact-form-standard';

import type { TypeFormInputs, TypePhoneCountry } from '@/types';
import type { FunctionComponent } from 'react';
import type { TypeContactFormBaseProps } from './types';

const PopUp = dynamic(() => import('@/atoms/pop-up-base'), { ssr: false });

/**
 * ContactForm
 */
export const ContactFormBase: FunctionComponent<TypeContactFormBaseProps> = ({
  textPolicies,
  buildingSelect,
  accommodationSelect,
  hearUsSelect,
  className,
  landing = false,
  defaultBuilding,
  defaultApartment,
  hearUsDefaultValue,
}: TypeContactFormBaseProps) => {
  const { t } = useTranslation('common');
  const methods = useForm<TypeFormInputs>();
  const [showModal, setShowModal] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [resetLandingForm, setResetLandingForm] = useState(false);
  const [phoneCountry, setPhoneCountry] = useState<TypePhoneCountry | null>(
    null
  );
  const Icon = hasError ? WarningCircle : CheckmarkCircle;
  const close = () => {
    setShowModal(false);
    setHasError(false);
  };

  const buildingsSelectValues = buildingSelect.map((building) => ({
    label: building.label,
    value: building.id,
    disabled: building.disabled,
  }));

  const mapOfAccommodation = new Map(accommodationSelect);
  const watchBuilding = methods.watch(
    'building',
    buildingsSelectValues?.[0]?.value.toString()
  );

  const validatePhone = (
    input: string,
    phoneCountry: TypePhoneCountry | null
  ): { isValid: boolean; e164Phone: string; isMobile: boolean } => {
    if (!phoneCountry) {
      return { isValid: false, e164Phone: input, isMobile: false };
    }

    const stripped = input.replace(/[\s\-().]+/g, '');
    const variants = [stripped];

    if (
      phoneCountry.countryCode.toLowerCase() === 'ar' &&
      stripped.startsWith('54') &&
      stripped[2] !== '9'
    ) {
      const mobileVersion = `${stripped.slice(0, 2)}9${stripped.slice(2)}`;
      variants.push(mobileVersion);
    }

    for (const variant of variants) {
      const parsed = parsePhoneNumberFromString(
        variant,
        phoneCountry.countryCode.toUpperCase()
      );
      if (parsed?.isValid()) {
        const isMobile = ['MOBILE', 'FIXED_LINE_OR_MOBILE'].includes(
          parsed.getType() ?? ''
        );
        return {
          isValid: true,
          e164Phone: parsed.number,
          isMobile,
        };
      }
    }

    methods.setError('telephone', {
      type: 'custom',
      message: t('components.organisms.contactForm.phoneError'),
    });

    return { isValid: false, e164Phone: stripped, isMobile: false };
  };

  const validateEmails = (email1: string, email2: string) => {
    const isValid = email1 === email2;
    if (!isValid) {
      methods.setError('emailConfirm', {
        type: 'custom',
        message: t('components.organisms.contactForm.emailError'),
      });
    }
    return isValid;
  };

  const onSubmit = async (data: TypeFormInputs) => {
    const { isValid, e164Phone } = validatePhone(data.telephone, phoneCountry);
    if (!isValid) return;

    if (!validateEmails(data.email, data.emailConfirm)) return;

    try {
      const selectedBuilding = buildingSelect.find(
        (building) => building.id.toString() === data.building
      );

      const payload = {
        name: data.name,
        lastName: data.lastName,
        email: data.email,
        phone: e164Phone, // ← correcto formato internacional
        description: data?.addMore ?? 'No se ha añadido descripción',
        marketingOptIn: data.marketingOptIn,
        marketingGreystar: data.marketingGreystar,
        hearUs: data.hearUs,
        building: {
          name: selectedBuilding?.label,
          id: selectedBuilding?.endpointId,
        },
        typeOfApartment: data.accommodation,
      };

      const response = await fetch('/api/lavanda', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const body = await response.json();

      if (response.ok) {
        gtmPush({
          event: 'generate_lead',
          lead_type: 'contact',
          provincia: '',
          phone_number: await sha256(e164Phone),
          edificio: selectedBuilding?.label,
          tipo_de_apartamento: data.accommodation,
          conocido: data.hearUs,
          address: {
            first_name: await sha256(data.name),
            last_name: await sha256(data.lastName),
          },
          email: await sha256(data.email),
        });

        setShowModal(true);
        methods.clearErrors();
        methods.reset();
        landing && setResetLandingForm(true);
      } else {
        throw new Error(JSON.stringify(body));
      }
    } catch (e) {
      setHasError(true);
      setShowModal(true);
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <form
          className={`
            relative flex w-full flex-col gap-4 rounded-md bg-white shadow-md ${
              landing ? 'p-5' : 'mt-6 p-6 pt-10 lg:p-16'
            }
            ${landing ? 'lg:h-fit lg:w-2/5 lg:max-w-[500px]' : ''}
            ${className}
          `}
          onSubmit={methods.handleSubmit(onSubmit)}
        >
          {landing ? (
            <ContactFormLanding
              textPolicies={textPolicies}
              buildingsSelectValues={buildingsSelectValues}
              mapOfAccommodation={mapOfAccommodation}
              watchBuilding={watchBuilding}
              hearUsSelect={hearUsSelect}
              defaultBuilding={defaultBuilding}
              defaultApartment={defaultApartment}
              hearUsDefaultValue={hearUsDefaultValue}
              methods={methods}
              validatePhone={validatePhone}
              validateEmails={validateEmails}
              resetForm={resetLandingForm}
              phoneCountry={phoneCountry}
              setPhoneCountry={setPhoneCountry}
            />
          ) : (
            <ContactFormStandard
              textPolicies={textPolicies}
              buildingsSelectValues={buildingsSelectValues}
              mapOfAccommodation={mapOfAccommodation}
              watchBuilding={watchBuilding}
              hearUsSelect={hearUsSelect}
              defaultBuilding={defaultBuilding}
              defaultApartment={defaultApartment}
              hearUsDefaultValue={hearUsDefaultValue}
              setPhoneCountry={setPhoneCountry}
            />
          )}
        </form>
      </FormProvider>
      <PopUp
        state={showModal}
        funcionOnClose={close}
        className="bg-surface-01 text-center"
      >
        <div className="flex max-w-md flex-col items-center justify-center gap-8">
          <Icon
            width="57"
            className={hasError ? 'text-status-error' : 'text-status-success'}
          />
          <div>
            <p className="text-heading-mobile--h3 mb-4 lg:text-heading-mobile--h2">
              {hasError
                ? t('components.organisms.contactForm.popUp.error.title')
                : t('components.organisms.contactForm.popUp.sucess.title')}
            </p>
            <p className="text-body--primary">
              {hasError
                ? t('components.organisms.contactForm.popUp.error.desc')
                : t('components.organisms.contactForm.popUp.sucess.desc')}
            </p>
          </div>
          <Button
            size="small"
            variant="secondary"
            onClick={close}
            className="max-w-max"
          >
            {hasError
              ? t('components.organisms.contactForm.popUp.error.button')
              : t('components.organisms.contactForm.popUp.sucess.button')}
          </Button>
        </div>
      </PopUp>
    </>
  );
};
ContactFormBase.displayName = 'ContactForm';
